import classNames from 'classnames';
import React, { useMemo, useEffect, useRef, type FC } from 'react';
import BaseButton from 'components/atoms/baseButton';
import { HashLink } from 'react-router-hash-link';

import { usePatientChartNavSection } from './use-patient-chart-nav-section';
import s from './styles.module.scss';
import {
  APP_HEADER_HEIGHT,
  PAGE_HEADER_HEIGHT,
  SCROLLING_DURATION,
  TOP_OFFSET,
} from './constants';

import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ModuleType } from 'infrastructure/enums';

interface PatientChartNavSectionProps {
  module: ModuleType;
  activityCount?: IRpmActivityCountResponse;
  isInHeader?: boolean;
  activeLink: string | null;
  onActiveLinkChange: (link: string | null) => void;
  isAutoScrollingRef: React.MutableRefObject<boolean>;
  hasInitializedRef?: React.MutableRefObject<boolean>;
}

const smoothScroll = (target: number, duration = SCROLLING_DURATION) => {
  const start = window.scrollY;
  const distance = target - start;
  let startTime: number | null = null;

  const scroll = (currentTime: number) => {
    if (!startTime) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const easing = progress * (2 - progress);
    window.scrollTo(0, start + distance * easing);

    if (timeElapsed < duration) {
      requestAnimationFrame(scroll);
    }
  };

  requestAnimationFrame(scroll);
};

export const scrollWithOffset = (
  el: HTMLElement,
  isInHeader: boolean = false,
) => {
  const yCoordinate = el.getBoundingClientRect().top + window.scrollY;
  const yOffset = isInHeader
    ? -(TOP_OFFSET + 1)
    : -(APP_HEADER_HEIGHT + PAGE_HEADER_HEIGHT - 1);
  smoothScroll(yCoordinate + yOffset);
};

const PatientChartNavSection: FC<PatientChartNavSectionProps> = ({
  module,
  activityCount,
  isInHeader = false,
  activeLink,
  onActiveLinkChange,
  isAutoScrollingRef,
  hasInitializedRef: externalHasInitializedRef,
}) => {
  const { linksList, generateLinksWithIndicators } =
    usePatientChartNavSection();
  const navWrapperRef = useRef<HTMLDivElement>(null);
  const activeButtonRef = useRef<HTMLAnchorElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const internalHasInitializedRef = useRef<boolean>(false);

  const hasInitializedRef =
    externalHasInitializedRef || internalHasInitializedRef;

  const links = useMemo(
    () => generateLinksWithIndicators(linksList, module, activityCount),
    [linksList, module, activityCount],
  );

  const scrollToHash = (hash: string, shouldStartFromTop = false) => {
    if (!hash) return;

    const sectionId = hash.replace('#', '');
    const element = document.getElementById(sectionId);

    if (element) {
      const linkExists = links.find((link) => link.to === hash);

      if (linkExists) {
        isAutoScrollingRef.current = true;
        onActiveLinkChange(hash);

        if (shouldStartFromTop) {
          window.scrollTo(0, 0);
        }

        const delay = shouldStartFromTop ? 200 : 50;
        setTimeout(() => {
          scrollWithOffset(element, isInHeader);

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          timeoutRef.current = setTimeout(() => {
            isAutoScrollingRef.current = false;
            timeoutRef.current = null;
          }, SCROLLING_DURATION + 90) as unknown as number;
        }, delay);
      }
    }
  };

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    const { hash } = window.location;

    if (hash) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      scrollToHash(hash, true);
    } else if (!activeLink) {
      onActiveLinkChange(links[0]?.to || null);
    }

    hasInitializedRef.current = true;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const { hash } = window.location;
      if (hash && hasInitializedRef.current) {
        scrollToHash(hash, false);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [links, isInHeader]);

  useEffect(() => {
    if (
      activeButtonRef.current &&
      navWrapperRef.current &&
      !isAutoScrollingRef.current
    ) {
      const button = activeButtonRef.current;
      const wrapper = navWrapperRef.current;

      const buttonRect = button.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      const isLeftOutside = buttonRect.left < wrapperRect.left;
      const isRightOutside = buttonRect.right > wrapperRect.right;

      if (isLeftOutside || isRightOutside) {
        const buttonOffsetLeft = button.offsetLeft;
        const buttonWidth = button.offsetWidth;
        const wrapperWidth = wrapper.offsetWidth;

        const scrollPosition =
          buttonOffsetLeft - wrapperWidth / 2 + buttonWidth / 2;

        wrapper.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }
    }
  }, [activeLink]);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: `-${TOP_OFFSET}px 0px 0px 0px`,
      threshold: [0.25, 0.5, 0.75, 1],
    };

    let currentIntersecting: { id: string; ratio: number }[] = [];

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      if (isAutoScrollingRef.current || !isInHeader) return;

      entries.forEach((entry) => {
        const id = entry.target.getAttribute('id');
        if (!id) return;

        if (entry.isIntersecting) {
          const existingIndex = currentIntersecting.findIndex(
            (item) => item.id === id,
          );
          if (existingIndex >= 0) {
            currentIntersecting[existingIndex].ratio = entry.intersectionRatio;
          } else {
            currentIntersecting.push({ id, ratio: entry.intersectionRatio });
          }
        } else {
          currentIntersecting = currentIntersecting.filter(
            (item) => item.id !== id,
          );
        }
      });

      if (currentIntersecting.length > 0) {
        const mostVisible = currentIntersecting.reduce((prev, current) =>
          current.ratio > prev.ratio ? current : prev,
        );

        onActiveLinkChange(`#${mostVisible.id}`);
      }
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    links.forEach((link) => {
      const sectionId = link.to.replace('#', '');
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
      currentIntersecting = [];
    };
  }, [links]);

  return (
    <div className={s.wrapper} ref={navWrapperRef}>
      {links.map((link) => {
        const isActive = activeLink === link.to;
        const buttonClassName = classNames(s['nav-button'], {
          [s['nav-button-unselected']]: !isActive,
          [s.marked]: link.marked && link.iconColor,
          [s[link.iconColor as string]]: link.iconColor,
        });

        return (
          <HashLink
            key={link.key}
            to={link.to}
            ref={isActive ? activeButtonRef : null}
            scroll={(el: HTMLElement) => {
              scrollWithOffset(el, isInHeader);
            }}
            onClick={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }

              isAutoScrollingRef.current = true;
              onActiveLinkChange(link.to);

              timeoutRef.current = setTimeout(() => {
                isAutoScrollingRef.current = false;
                timeoutRef.current = null;
              }, SCROLLING_DURATION + 90) as unknown as number;
            }}
          >
            <BaseButton
              label={link.title}
              type="primary"
              className={buttonClassName}
              data-cy={link.dataCy}
            />
          </HashLink>
        );
      })}
    </div>
  );
};

export default PatientChartNavSection;
